import { useState, useEffect, Suspense, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useHistory, Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import rolesUtils from 'app/roleBasedUtils';
import { userProfile, isAllowedToViewActivityMetrics } from 'utils/auth';

import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  salesDemoActions,
  reducer,
  sliceKey,

} from 'app/pages/SalesPortalPage/LayoutV1/slice';
import { watchFetchSalesDemo } from 'app/pages/SalesPortalPage/LayoutV1/saga';
import {
  selectMetrics,
  selectMockApiFlag,
  selectTimeperiod,
  selectDataGridData,
  selectTaskHtml,
  selectExportData,
  selectMysqlFlag,
} from 'app/pages/SalesPortalPage/LayoutV1/selectors';
import { selectDealerSelected } from '../../redux/headerSlice/selectors';
import { useStyles } from 'app/components/GlobalStyles';
import LoadingIndicator from '../../sharedComponents/LoadingIndicator/index';
import {
  // DataTableContainer,
  PageContainer,
  PageHeader,
  BreadcrumbContainer,
  BreadcrumbLinkComponent,
} from '../../sharedComponents/StyledComponents';
// import { BreadcrumbsComponent } from 'app/components/BreadcrumbsComponent/Loadable';
import { leadNurturingTasksActions } from 'app/pages/LeadNurturingTasksPage/slice';
import {
  SalesDemoToolbar,
  // NurturingMetricsComponent,
  // SalesDemoDataGridComponent,
} from 'app/pages/SalesPortalPage/LayoutV1/components/Loadable';
import { NurturingMetricsComponent } from './components/NurturingMetricsComponent';
import {
  metricTitleEnum,
  metricTypeEnum,
  nurturingStatusEnum,
  papeTitleEnum,
  renderComponentEnum,
} from 'app/pages/SalesPortalPage/LayoutV1/data/type';

import { Box, Breadcrumbs, Typography, Button } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { withResourceProtection } from 'app/components/ReusableUIComponents/HOCs/ResourceProtected/withResourceProtection';
import DataGridMAIQ from '../../sharedComponents/DataGridMAIQ';
import DataGridTypes from 'types/DataGridTypes';
import CarouselMAIQ, { CarouselCardData } from '../../sharedComponents/CarouselMAIQ';
import { useQuery } from '@tanstack/react-query';
import Lead from 'app/apiService/lead/leadApiService';
import Activity, {
  ActivityType,
} from 'app/apiService/activity/activityApiService';
import { handleNurturingHistoryRedirect } from './utils/nurturingHistoryUtils';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { ExecutiveSummary } from './components/ExecutiveSummary/ExecutiveSummary';
import { useSummarization } from 'app/context/SummarizationContext';

type breadcrumbProps = {
  label: string;
  link: string;
  isActive: boolean;
  componentLinked: string;
  filter: {
    metricType?: string;
    metricName?: string;
    groupId?: string;
    opportunityId?: string;
  };
};

type LocationStateWithBreadcrumbs = { breadcrumbsList?: breadcrumbProps[] };

// Helper function to get initial breadcrumbs based on current path
const getBreadcrumbsInitial = (pathname: string): breadcrumbProps[] => {
  const isAIAvatarLayout = pathname.includes('/ai-avatar-layout');
  return [
    {
      label: `Metrics View`,
      link: isAIAvatarLayout ? `/ai-avatar-layout/measure` : `/sales-demo`,
      isActive: true,
      componentLinked: renderComponentEnum.metrics,
      filter: {},
    },
  ];
};

// Sample data for carousel
const sampleCarouselData: CarouselCardData[] = [
  {
    _id: 1,
    content: '##title::John Smith##subtitle::Walk-in##text::Mar 31, 2025##',
  },
  {
    _id: 2,
    content: '##title::Mary Kay##subtitle::Phone-up##text::Mar 31, 2025##',
  },
  {
    _id: 3,
    content: '##title::Jim Moore##subtitle::Website##text::Mar 30, 2025##',
  },
  {
    _id: 4,
    content: '##title::Lary Page##subtitle::Website##text::Mar 29, 2025##',
  },
  {
    _id: 5,
    content: '##title::Elon Musk##subtitle::Walk-in##text::Mar 28, 2025##',
  },
  {
    _id: 6,
    content: '##title::Ada Lovelace##subtitle::Phone-up##text::Mar 27, 2025##',
  },
];

// Data for Recent Sales Influenced
const salesInfluencedData: CarouselCardData[] = [
  {
    _id: 1,
    content: '##title::Tyler Rose##subtitle::2025 CR-V##text::Mar 31, 2025##',
  },
  {
    _id: 2,
    content:
      '##title::Nick Wick##subtitle::2025 Ridgeline##text::Mar 28, 2025##',
  },
  {
    _id: 3,
    content: '##title::Jim Holden##subtitle::2025 CR-V##text::Mar 31, 2025##',
  },
  {
    _id: 4,
    content: '##title::Alex Miller##subtitle::2021 Civic##text::Mar 31, 2025##',
  },
  {
    _id: 5,
    content: '##title::Alex Miller##subtitle::2021 Civic##text::Mar 31, 2025##',
  },
];

// Mock data for Active Outreach - Will be replaced with activityContext.activeOutreach
// const activeOutreachData: CarouselCardData[] = [ ... ];

// Mock data for Recent Revivals - Will be replaced with activityContext.recentRevivals
const mockRecentRevivals = [
  {
    _id: 'revival_1',
    content:
      '##title::John Smith##subtitle::CONTACTED##text::Apr 1, 2025##tooltip::Called customer about Honda CR-V inquiry. Customer interested in test drive next week.##',
  },
  {
    _id: 'revival_2',
    content:
      '##title::Mary Johnson##subtitle::CONTACTED##text::Apr 1, 2025##tooltip::Follow-up call about Civic financing options. Customer requested more details about current promotions.##',
  },
  {
    _id: 'revival_3',
    content:
      '##title::David Lee##subtitle::CONTACTED##text::Mar 31, 2025##tooltip::Discussed Accord features. Customer particularly interested in hybrid model.##',
  },
  {
    _id: 'revival_4',
    content:
      '##title::Sarah Chen##subtitle::CONTACTED##text::Mar 31, 2025##tooltip::Scheduled test drive for Pilot. Customer looking for family SUV with good safety features.##',
  },
  {
    _id: 'revival_5',
    content:
      '##title::Mike Wilson##subtitle::CONTACTED##text::Mar 30, 2025##tooltip::Discussed HR-V features and pricing. Customer comparing with competitors.##',
  },
];

// Mock data for Recent Sales Influenced - Will be replaced with activityContext.recentSalesInfluenced
const mockSalesInfluenced = [
  {
    _id: 'sale_1',
    content:
      '##title::Sarah Wilson##subtitle::2025 CR-V EX##text::Apr 1, 2025##tooltip::Customer purchased CR-V after initial inquiry through website. Influenced by detailed feature comparison provided.##',
  },
  {
    _id: 'sale_2',
    content:
      '##title::James Brown##subtitle::2025 Pilot Elite##text::Mar 31, 2025##tooltip::Sale completed after test drive. Customer appreciated the follow-up on available inventory.##',
  },
  {
    _id: 'sale_3',
    content:
      '##title::Emily Davis##subtitle::2025 Civic Sport##text::Mar 31, 2025##tooltip::Customer finalized purchase after receiving competitive financing options.##',
  },
  {
    _id: 'sale_4',
    content:
      '##title::Michael Chang##subtitle::2025 Accord Hybrid##text::Mar 30, 2025##tooltip::Customer chose Accord Hybrid after detailed fuel efficiency comparison.##',
  },
  {
    _id: 'sale_5',
    content:
      '##title::Lisa Rodriguez##subtitle::2025 HR-V##text::Mar 30, 2025##tooltip::First-time buyer convinced by safety features and compact size.##',
  },
];

// TODO: Replace these with activity context state
// const recentRevivalsMapped = activityContext.recentRevivals;
// const recentSalesInfluencedMapped = activityContext.recentSalesInfluenced;

// Use mock data instead of API response for now
// const recentRevivalsMapped = mockRecentRevivals;

// Ask Mia Button Component
type AskMiaButtonProps = {
  onClick: () => void;
};

const AskMiaButtonComponent = ({ onClick }: AskMiaButtonProps) => {
  return (
    <Button
      size="large"
      onClick={onClick}
      sx={{
        borderColor: 'primary.main',
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        '&:hover': {
          borderColor: 'primary.dark',
          backgroundColor: 'primary.dark',
          color: 'primary.contrastText',
        },
      }}
    >
      Ask Mia
    </Button>
  );
};

// Wrap with resource protection HOC
const ProtectedAskMiaButton = withResourceProtection(
  AskMiaButtonComponent,
  'AskMia', // Resource identifier - adjust based on your resource_mappings
  null // Return null if no access (button won't be shown)
);

// Group Dashboard Button Component
type GroupDashboardButtonProps = {
  selectedDealer: any;
};

const GroupDashboardButtonComponent = ({ selectedDealer }: GroupDashboardButtonProps) => {
  return (
    <Button
      variant="outlined"
      size="large"
      disabled={!selectedDealer?.business_group_dashboard_url}
      onClick={() => {
        if (selectedDealer?.business_group_dashboard_url) {
          window.open(selectedDealer.business_group_dashboard_url, '_blank');
        }
      }}
      sx={{
        borderColor: 'primary.main',
        color: 'primary.main',
        '&:hover': {
          borderColor: 'primary.dark',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
        },
        '&:disabled': {
          borderColor: 'text.disabled',
          color: 'text.disabled',
        },
      }}
    >
      Group Dashboard
    </Button>
  );
};

// Wrap with resource protection HOC
const ProtectedGroupDashboardButton = withResourceProtection(
  GroupDashboardButtonComponent,
  'GroupDashboardButton', // Resource identifier - adjust based on your resource_mappings
  null // Return null if no access (button won't be shown)
);

export const MeasurePage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();

  // dynamically inject reducer and saga
  useInjectReducer({ key: sliceKey, reducer: reducer }); // sliceKey and reducer imported from slice.ts
  useInjectSaga({ key: sliceKey, saga: watchFetchSalesDemo });

  const userDetails = userProfile();
  const selectedDealer = useSelector(selectDealerSelected);
  const selectedDealerId = selectedDealer?.dealer || '';
  const mockApiFlag = useSelector(selectMockApiFlag);
  const mysqlFlag = useSelector(selectMysqlFlag);
  const metricsState = useSelector(selectMetrics);
  const { conversation_id, loading: summarizationLoading, clearStreamingText, resetState } = useSummarization();
  const previousDealerIdRef = useRef<string>('');
  
  // Debug: Log conversation_id changes
  useEffect(() => {
    console.log('MeasurePage - conversation_id from context:', conversation_id);
  }, [conversation_id]);

  // Reset executive summary and clear metrics when dealer changes
  useEffect(() => {
    // Only reset if dealer actually changed (not on initial mount)
    if (selectedDealerId && previousDealerIdRef.current !== selectedDealerId) {
      // Skip reset on initial mount when previousDealerIdRef is empty
      if (previousDealerIdRef.current !== '') {
        console.log('🔄 Dealer changed, resetting executive summary and clearing metrics. Previous:', previousDealerIdRef.current, 'New:', selectedDealerId);
        resetState();
        // Clear metrics data when dealer changes to prevent showing old data
        dispatch(salesDemoActions.clearMetrics());
      }
      // Update the ref to track current dealer
      previousDealerIdRef.current = selectedDealerId;
    } else if (selectedDealerId) {
      // Update ref even if no reset needed (for initial mount)
      previousDealerIdRef.current = selectedDealerId;
    }
  }, [selectedDealerId, resetState, dispatch]);
  const timeperiodState = useSelector(selectTimeperiod);
  const dataGridDataState = useSelector(selectDataGridData);
  const taskHtmlState = useSelector(selectTaskHtml);
  const exportDataState = useSelector(selectExportData);
  const metrics = mockApiFlag
    ? metricsState.data || []
    : metricsState.data?.body?.metrics || [];
  const timeperiod = mockApiFlag
    ? timeperiodState.data || []
    : timeperiodState.data || [];
  const gridData = mockApiFlag
    ? dataGridDataState.data?.data || []
    : dataGridDataState.data?.body?.data || [];
  const dynamicHeaders = mockApiFlag
    ? dataGridDataState.data?.headers || []
    : dataGridDataState.data?.body?.headers || [];
  const taskHtml = mockApiFlag
    ? taskHtmlState.data || {}
    : taskHtmlState.data || {};

  // localStorage keys for time period (per-dealer + recent list + default set only at login)
  const TIME_PERIOD_STORAGE_KEY = 'salesDemo_timePeriod';
  const TIME_PERIOD_DEFAULT_KEY = 'salesDemo_timePeriod_default';
  const RECENT_TIMEPERIODS_KEY = 'recent_timeperiods';

  // Function to get default time period set at login (env fallback)
  const getDefaultTimePeriodFromLogin = () => {
    try {
      return localStorage.getItem(TIME_PERIOD_DEFAULT_KEY) || null;
    } catch {
      return null;
    }
  };

  // Default time period: dealer preferences.default_time_period from dealers API first, then env (login localStorage) fallback
  const getDefaultTimePeriodCode = (): string | null => {
    const dealerDefault = selectedDealer?.preferences?.default_time_period;
    if (dealerDefault && typeof dealerDefault === 'string' && dealerDefault.trim()) {
      return dealerDefault.trim();
    }
    return getDefaultTimePeriodFromLogin();
  };

  // Function to get most recently selected time period from TimeperiodSelect's recent list (same key as dropdown uses)
  const getRecentTimeperiodFromStorage = (): { code: string; startDate?: string; endDate?: string } | null => {
    try {
      const stored = localStorage.getItem(RECENT_TIMEPERIODS_KEY);
      if (!stored) return null;
      const arr = JSON.parse(stored);
      const first = Array.isArray(arr) && arr.length ? arr[0] : null;
      if (first && first.code) {
        return {
          code: first.code,
          startDate: first.startDate,
          endDate: first.endDate,
        };
      }
      return null;
    } catch {
      return null;
    }
  };

  // Function to get stored time period for current dealer
  const getStoredTimePeriod = (dealerId: string) => {
    try {
      const stored = localStorage.getItem(TIME_PERIOD_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        return data[dealerId] || null;
      }
    } catch (error) {
      console.error('Error reading time period from localStorage:', error);
    }
    return null;
  };

  // Function to store time period for current dealer
  const storeTimePeriod = (dealerId: string, timePeriod: string, startDate?: string, endDate?: string) => {
    try {
      const stored = localStorage.getItem(TIME_PERIOD_STORAGE_KEY);
      const data = stored ? JSON.parse(stored) : {};
      data[dealerId] = {
        timePeriod,
        startDate,
        endDate
      };
      localStorage.setItem(TIME_PERIOD_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error writing time period to localStorage:', error);
    }
  };

  // Function to clear stored time period when dealer changes
  const clearStoredTimePeriod = (dealerId: string) => {
    try {
      const stored = localStorage.getItem(TIME_PERIOD_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        delete data[dealerId];
        localStorage.setItem(TIME_PERIOD_STORAGE_KEY, JSON.stringify(data));
      }
    } catch (error) {
      console.error('Error clearing time period from localStorage:', error);
    }
  };

  // Debug function to check localStorage data
  const debugLocalStorage = () => {
    try {
      const stored = localStorage.getItem(TIME_PERIOD_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        return data;
      }
    } catch (error) {
      console.error('Error reading localStorage for debug:', error);
    }
    return null;
  };

  // Configure which API calls should exclude the mysql parameter
  useEffect(() => {
    // For active outreach, exclude mysql parameter from the main API call
    dispatch(
      salesDemoActions.updateExcludeMysqlParams({
        apiType: 'activeOutreach',
        exclude: true,
      }),
    );

    // Return cleanup function to reset when component unmounts
    return () => {
      // Clear any exclusions when component unmounts
      dispatch(
        salesDemoActions.updateExcludeMysqlParams({
          apiType: 'activeOutreach',
          exclude: false,
        }),
      );
    };
  }, [dispatch]);

  const [timeperiodSelect, setTimeperiodSelect] = useState('');
  const [previousDealerId, setPreviousDealerId] = useState<string>('');

  const [renderComponent, setRenderComponent] = useState(
    renderComponentEnum.metrics,
  );
  const [metricFilter, setMetricFilter] = useState<string>('');
  const [breadcrumbsList, setBreadcrumbslist] = useState<breadcrumbProps[]>(
    getBreadcrumbsInitial(location.pathname),
  );
  const [expandedAccordion, setExpandedAccordion] = useState<string[]>([]);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState('');
  const [pageTitle, setPageTitle] = useState(papeTitleEnum.default);
  const [renderData, setRenderData] = useState({});
  const [selectedBox, setSelectedBox] = useState(-1);
  const [dates, setDates] = useState({ start_date: '', end_date: '' });
  const hasRestoredFromBreadcrumbState = useRef(false);

  // Cleanup effect for time period persistence
  useEffect(() => {
    return () => {
      // Store current dealer's time period when component unmounts
      if (selectedDealerId && timeperiodSelect) {
        storeTimePeriod(selectedDealerId, timeperiodSelect, dates.start_date, dates.end_date);
      }
    };
  }, [selectedDealerId, timeperiodSelect, dates.start_date, dates.end_date]);

  // Use modified activeOutreachParams - remove mysql param since it will be excluded
  const activeOutreachParams = {
    dealer_id: selectedDealerId,
    ...(timeperiodSelect === 'CUSTOM_RANGE'
      ? { startDate: dates.start_date, endDate: dates.end_date }
      : { timeperiod: timeperiodSelect }),
    skip: 0,
    limit: 20,
    activityType: 'ACTIVE_OUTREACH' as ActivityType,
    // mysql param is intentionally excluded from here
  };

  const {
    data: activitiesData,
    isLoading: isActivitiesLoading,
    error: activitiesError,
    isRefetching: isActivitiesRefetching,
    dataUpdatedAt: activitiesUpdatedAt,
    refetch: refetchActivities,
  } = useQuery({
    queryKey: ['active-outreach', activeOutreachParams],
    queryFn: () => Activity.getActivities(activeOutreachParams),
    enabled: !!selectedDealerId,
    refetchInterval: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Map context data for Active Outreach
  const activeOutreachMapped = Array.isArray(activitiesData?.body)
    ? activitiesData?.body.map(item => ({
        _id: item._id,
        content:
          typeof item.content === 'object'
            ? `##title::${item.content.title || ''}##subtitle::${
                item.content.sub_title || ''
              }##text::${item.content.text || ''}##`
            : `##text::${item.content || ''}##`,
      }))
    : [];

  // listening to dealerId, mockApiFlag and mysqlFlag
  useEffect(() => {
    // Check if dealer has changed
    if (previousDealerId && previousDealerId !== selectedDealerId) {
      // Dealer changed - store previous dealer's time period
      if (timeperiodSelect) {
        storeTimePeriod(previousDealerId, timeperiodSelect, dates.start_date, dates.end_date);
      }
      // Clear current time period state for new dealer
      setTimeperiodSelect('');
      setDates({ start_date: '', end_date: '' });
    } else if (selectedDealerId && timeperiodSelect) {
      // Same dealer - store current time period
      storeTimePeriod(selectedDealerId, timeperiodSelect, dates.start_date, dates.end_date);
    }
    
    // Update previous dealer ID
    setPreviousDealerId(selectedDealerId);
    
    // Only call handleSelectDealer if dealer actually changed
    if (previousDealerId && previousDealerId !== selectedDealerId) {
      handleSelectDealer();
    }
  }, [selectedDealerId, mockApiFlag, mysqlFlag]);

  // Initialize previous dealer ID on first load
  useEffect(() => {
    if (selectedDealerId && !previousDealerId) {
      setPreviousDealerId(selectedDealerId);
      const incomingBreadcrumbs = (location.state as LocationStateWithBreadcrumbs)?.breadcrumbsList;
      if (incomingBreadcrumbs?.length) {
        // Coming back from breadcrumb (e.g. Nurturing History page) – restore state in separate effect
        // Only request timeperiod here; do not reset breadcrumbs/renderComponent
        dispatch(
          salesDemoActions.requestTimeperiod({
            dealershipId: selectedDealerId,
          }),
        );
      } else {
        handleSelectDealer();
      }
    }
  }, [selectedDealerId, previousDealerId]);

  // Deduplicate consecutive breadcrumbs (same label + componentLinked) to fix repeated trail
  const deduplicateBreadcrumbs = (items: any[]): any[] => {
    if (!items?.length) return items;
    return items.filter((item, i) => {
      if (i === 0) return true;
      const prev = items[i - 1];
      return !(prev?.label === item?.label && prev?.componentLinked === item?.componentLinked);
    });
  };

  // Restore Measure page state when navigating back via breadcrumb (e.g. from Shareable Nurturing History)
  useEffect(() => {
    const incomingBreadcrumbs = (location.state as LocationStateWithBreadcrumbs)?.breadcrumbsList;
    if (
      hasRestoredFromBreadcrumbState.current ||
      !incomingBreadcrumbs?.length ||
      !selectedDealerId ||
      !timeperiodSelect
    ) {
      return;
    }
    const deduped = deduplicateBreadcrumbs(incomingBreadcrumbs);
    const lastBreadcrumb = deduped[deduped.length - 1];
    const componentLinked = lastBreadcrumb.componentLinked;
    const filter = lastBreadcrumb.filter || {};
    if (!componentLinked) {
      history.replace(location.pathname, {});
      hasRestoredFromBreadcrumbState.current = true;
      return;
    }
    hasRestoredFromBreadcrumbState.current = true;
    let navLinks = deduped.map((item: any, idx: number) => ({
      ...item,
      isActive: idx === deduped.length - 1,
      link: item.link?.includes('/sales-demo')
        ? item.link.replace('/sales-demo', '/ai-avatar-layout/measure')
        : item.link || '/ai-avatar-layout/measure',
    }));
    if (selectedDealer && navLinks[0]) {
      navLinks[0] = {
        ...navLinks[0],
        label: rolesUtils.isDealershipUser(userDetails.roles)
          ? selectedDealer.name
          : selectedDealer.abbreviation || selectedDealer.name || '',
      };
    }
    setBreadcrumbslist(navLinks);
    testRenderComponent(componentLinked, filter, navLinks, true);
    history.replace(location.pathname, {});
  }, [location.state, selectedDealerId, timeperiodSelect]);

  // Time period only from localStorage: per-dealer stored value, or default set at login (no default on page)
  useEffect(() => {
    if (!timeperiod.length || !selectedDealerId || summarizationLoading || !conversation_id || timeperiodSelect) {
      return;
    }

    const stored = getStoredTimePeriod(selectedDealerId);
    const recent = getRecentTimeperiodFromStorage();
    let codeToUse: string | null = null;
    let startDateToUse: string | undefined;
    let endDateToUse: string | undefined;

    // Prefer recent_timeperiods (what user last selected in dropdown) so UI matches localStorage
    if (recent?.code && timeperiod.some(t => t.code === recent.code)) {
      codeToUse = recent.code;
      startDateToUse = recent.startDate;
      endDateToUse = recent.endDate;
      storeTimePeriod(selectedDealerId, recent.code, recent.startDate, recent.endDate);
    } else if (stored?.timePeriod && timeperiod.some(t => t.code === stored.timePeriod)) {
      codeToUse = stored.timePeriod;
      startDateToUse = stored.startDate;
      endDateToUse = stored.endDate;
    } else {
      // Dealer preferences.default_time_period first, then env (login) fallback
      const defaultCode = getDefaultTimePeriodCode();
      if (defaultCode && timeperiod.some(t => t.code === defaultCode)) {
        codeToUse = defaultCode;
        storeTimePeriod(selectedDealerId, defaultCode, undefined, undefined);
      }
    }

    if (!codeToUse) {
      return;
    }

    setTimeperiodSelect(codeToUse);
    if (codeToUse === 'CUSTOM_RANGE' && startDateToUse && endDateToUse) {
      setDates({ start_date: startDateToUse, end_date: endDateToUse });
    }

    const metricsPayload: any = {
      dealershipId: selectedDealerId,
      timeperiod: codeToUse,
    };
    if (codeToUse === 'CUSTOM_RANGE' && startDateToUse && endDateToUse) {
      metricsPayload.startDate = startDateToUse;
      metricsPayload.endDate = endDateToUse;
    }
    if (conversation_id) {
      metricsPayload.conversation_id = conversation_id;
    }
    dispatch(salesDemoActions.requestMetrics(metricsPayload));
  }, [timeperiod, selectedDealerId, conversation_id, summarizationLoading, timeperiodSelect]);

  // on dealer selection
  // requesting timeperiod available for the dealership
  const handleSelectDealer = () => {
    try {
      setSelectedBox(-1);
      if (selectedDealerId) {
        dispatch(
          salesDemoActions.requestTimeperiod({
            dealershipId: selectedDealerId,
          }),
        );
        let breadcrumb = getBreadcrumbsInitial(location.pathname);
        breadcrumb[0].label = rolesUtils.isDealershipUser(userDetails.roles)
          ? selectedDealer.name
          : selectedDealer.abbreviation || selectedDealer.name || '';
        setBreadcrumbslist(breadcrumb);
        setRenderComponent(renderComponentEnum.metrics);
        setMetricFilter('');
      }
    } catch (error) {
      console.log(error);
    }
  };

  // storing timeperiod selection and making API call
  const handleClickChangeTimeperiod = (
    timeperiodValue: string,
    label: string,
    startDate?: string,
    endDate?: string,
  ) => {
    try {
      // Clear executive summary text when time period changes
      clearStreamingText();
      
      setTimeperiodSelect(timeperiodValue);
      
      // Store the selected time period in localStorage
      storeTimePeriod(selectedDealerId, timeperiodValue, startDate, endDate);

      // If startDate and endDate are provided (for custom date range)
      if (startDate && endDate) {
        setDates({ start_date: startDate, end_date: endDate });
        dispatch(
          salesDemoActions.requestMetrics({
            dealershipId: selectedDealerId,
            timeperiod: timeperiodValue,
            startDate,
            endDate,
            ...(conversation_id && { conversation_id }),
          }),
        );
      } else {
        dispatch(
          salesDemoActions.requestMetrics({
            dealershipId: selectedDealerId,
            timeperiod: timeperiodValue,
            ...(conversation_id && { conversation_id }),
          }),
        );
      }

      // Handle breadcrumbs update if needed
      if (breadcrumbsList.length > 1) {
        let breadcrumbIndex = breadcrumbsList.length - 1;
        let breadcrumb = breadcrumbsList[breadcrumbIndex];
        let navLinks = breadcrumbsList.splice(0, breadcrumbIndex);

        testRenderComponent(
          breadcrumb.componentLinked,
          breadcrumb.filter,
          navLinks,
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  // toggling mock api usage
  const handleToggleMockData = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(salesDemoActions.toggleUseMockData(event.target.checked));
  };

  // toggling the use of mysql DB
  const handleToggleMysql = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(salesDemoActions.toggleUseMysql(event.target.checked));
  };

  // traversing through the breadcrumbs if its not the current component
  // isActive attribute indicates the current component being rendered
  //
  const handleClickBreadcrumbs = (breadcrumb, index) => {
    try {
      if (breadcrumb.isActive) {
        return;
      }
      let navLinks = [...breadcrumbsList];
      navLinks = navLinks.splice(0, index ? index : index + 1);
      navLinks = navLinks.map((navLink, navIndex) =>
        navIndex < navLinks.length - 1
          ? { ...navLink, isActive: false }
          : { ...navLink, isActive: true },
      );

      testRenderComponent(
        breadcrumb.componentLinked,
        breadcrumb.filter,
        navLinks,
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleChangeExpandedAccordion =
    (tasks: any[]) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      try {
        let taskIds = tasks.map(task =>
          mysqlFlag ? task['Message ID'] : task._id,
        );

        if (!isExpanded) {
          let ids =
            expandedAccordion?.filter(id => !taskIds?.includes(id)) || [];

          setExpandedAccordion(ids || []);
        } else {
          setExpandedAccordion(taskIds || []);
          getTaskHtml(tasks || []);
        }
        return;
      } catch (error) {
        console.log(error);
      }
    };

  const testRenderComponent = (
    component,
    filter,
    updatedBreadcrumbs: any | null = null,
    isRestoreFromBreadcrumb = false,
  ) => {
    try {
      const { metricType, metricName, groupId, opportunityId, rowDetails } =
        filter || {};

      // Handle nurturing history case first - don't deselect card and redirect immediately
      if (component === renderComponentEnum.nurturingHistory) {
        // Use new short URL redirect functionality
        handleNurturingHistoryRedirect(
          {
            emailAddress:
              rowDetails?.email_address ||
              rowDetails?.emailAddress ||
              rowDetails?.Email_mailshake ||
              rowDetails?.email,
            opportunityId: opportunityId,
            dealerId: selectedDealerId,
            analytics: true,
            rowDetails: rowDetails,
          },
          dispatch,
          history,
          breadcrumbsList,
        );

        // No need to update breadcrumbs or component since we're redirecting
        return;
      }

      // For other components, deselect the card
      setSelectedBox(-1);
      
      const segmentName = rowDetails?.name || '';
      const leadDetails = rowDetails?.name || '';
      let navLink: any = updatedBreadcrumbs
        ? [...updatedBreadcrumbs]
        : [...breadcrumbsList];

      let requestData: any = {
        dealershipId: selectedDealerId,
        timeperiod: timeperiodSelect,
      };

      switch (component) {
        case renderComponentEnum.metrics:
          setRenderComponent(renderComponentEnum.metrics);
          setMetricFilter('');
          // Store the current time period when navigating back to metrics
          if (timeperiodSelect) {
            storeTimePeriod(selectedDealerId, timeperiodSelect, dates.start_date, dates.end_date);
          }
          // Always fetch metrics so metrics + executive summary show (including on breadcrumb restore)
          if (timeperiodSelect === 'CUSTOM_RANGE' && dates.start_date && dates.end_date) {
            dispatch(
              salesDemoActions.requestMetrics({
                dealershipId: selectedDealerId,
                timeperiod: timeperiodSelect,
                startDate: dates.start_date,
                endDate: dates.end_date,
                ...(conversation_id && { conversation_id }),
              }),
            );
          } else {
            dispatch(
              salesDemoActions.requestMetrics({
                dealershipId: selectedDealerId,
                timeperiod: timeperiodSelect,
                ...(conversation_id && { conversation_id }),
              }),
            );
          }
          break;

        case renderComponentEnum.dataGrid:
          if (!isRestoreFromBreadcrumb) {
            dispatch(salesDemoActions.clearDatagridData());
          }
          requestData = {
            ...requestData,
            filterType: metricType,
            data: {},
          };

          const lastAlreadyMatches =
            isRestoreFromBreadcrumb &&
            navLink.length > 0 &&
            navLink[navLink.length - 1]?.componentLinked === renderComponentEnum.dataGrid;

          if (metricType === metricTypeEnum.opportunityAddedInNurturing) {
            setSelectedBox(0);
            requestData.data = {
              lead_filter: {
                ...(filter.segment
                  ? { segment: filter.segment }
                  : {
                      nurturing_status: {
                        $in: [
                          nurturingStatusEnum.underNurturing,
                          nurturingStatusEnum.contacted,
                          nurturingStatusEnum.sold,
                        ],
                      },
                    }),
              },
              timeperiod: timeperiodSelect,
            };
            if (!lastAlreadyMatches) {
              navLink.push({
                label: metricName || metricTitleEnum.opportunityAddedInNurturing,
                link: `/sales-demo`,
                isActive: true,
                componentLinked: renderComponentEnum.dataGrid,
                filter,
              });
            }
          } else if (metricType === metricTypeEnum.leadContacted) {
            setSelectedBox(4);

            requestData.data = {
              lead_filter: {
                nurturing_status: nurturingStatusEnum.contacted,
              },
              timeperiod: timeperiodSelect,
            };
            if (!lastAlreadyMatches) {
              navLink.push({
                label: `${metricName || metricTitleEnum.leadContacted} ${
                  segmentName ? `(${segmentName})` : ''
                }`,
                link: `/sales-demo`,
                isActive: true,
                componentLinked: renderComponentEnum.dataGrid,
                filter,
              });
            }
          } else if (metricType === metricTypeEnum.leadSold) {
            setSelectedBox(5);

            requestData.data = {
              lead_filter: {
                nurturing_status: nurturingStatusEnum.sold,
              },
              timeperiod: timeperiodSelect,
            };
            if (!lastAlreadyMatches) {
              navLink.push({
                label: `${metricName || metricTitleEnum.leadSold} ${
                  segmentName ? `(${segmentName})` : ''
                }`,
                link: `/sales-demo`,
                isActive: true,
                componentLinked: renderComponentEnum.dataGrid,
                filter,
              });
            }
          } else if (metricType === metricTypeEnum.segment) {
            setSelectedBox(1);

            requestData.data = {
              timeperiod: timeperiodSelect,
            };
            if (!lastAlreadyMatches) {
              navLink.push({
                label: metricName || metricTitleEnum.segment,
                link: `/sales-demo`,
                isActive: true,
                componentLinked: renderComponentEnum.dataGrid,
                filter,
              });
            }
          }

          if (groupId) {
            requestData.data = {
              ...requestData.data,
              group_entity_filter: {
                group_id: groupId,
              },
              timeperiod: timeperiodSelect,
            };
          }

          // dispatch(salesDemoActions.requestDataGridData(requestData));
          setRenderData(requestData);
          setRenderComponent(renderComponentEnum.dataGrid);
          setMetricFilter(metricType);
          break;

        default:
          break;
      }

      navLink = navLink?.map((item, index) =>
        index < navLink.length - 1
          ? { ...item, isActive: false }
          : { ...item, isActive: true },
      );

      setBreadcrumbslist(prev => navLink);
    } catch (error) {
      console.log(error);
    }
  };

  const getTaskHtml = tasks => {
    try {
      let taskIds = tasks.map(task =>
        mysqlFlag ? task['Message ID'] : task._id,
      );
      
      // Filter out tasks that already have HTML or have s3_url (handled by NurturingHistoryMessageContainer)
      let ids = taskIds?.filter(taskId => {
        // Skip if HTML already exists
        if (taskHtml[taskId]) return false;
        
        // Find the corresponding task object
        const task = tasks.find(t => (mysqlFlag ? t['Message ID'] : t._id) === taskId);
        
        // Skip if task has s3_url (will be handled by NurturingHistoryMessageContainer)
        if (task?.s3_url && task.s3_url.trim() !== '') {
          console.log(`Task ${taskId} has s3_url, skipping API call in SalesDemoPage`);
          return false;
        }
        
        return true;
      });

      if (ids?.length) {
        dispatch(
          salesDemoActions.requestTaskHtml({
            mysqlFlag,
            data: { task_ids: ids, email: tasks[0]._id || '' },
          }),
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleExportData = (jsonObjects, fileName) => {
    try {
      if (Object.keys(jsonObjects).length) {
        dispatch(
          salesDemoActions.requestExportData({
            jsonObjects,
            fileName: fileName || `nurturing_history_data`,
          }),
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const clearNurturingHistoryDetails = () => {
    try {
      setPageTitle(papeTitleEnum.default);
      dispatch(salesDemoActions.clearNurturingHistory());
      dispatch(salesDemoActions.clearTaskHtml());
      setExpandedAccordion([]);
    } catch (error) {
      console.log(error);
    }
  };

  // Date formatter utility
  const dateFormatter = (date: Date | string | undefined | null) => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const handleCellCLick = cell => {
    if (cell.field === 'no_of_leads') {
      testRenderComponent(renderComponentEnum.dataGrid, {
        metricType: metricTypeEnum.opportunityAddedInNurturing,
        rowDetails: cell.row || {},
        groupId: cell?.row?._id || '',
        segment: cell?.row?.segment || '',
      });
      setSelectedBox(0);
    }
    if (cell.field === metricTypeEnum.leadContacted) {
      testRenderComponent(renderComponentEnum.dataGrid, {
        metricType: metricTypeEnum.leadContacted,
        rowDetails: cell.row || {},
        groupId: cell?.row?._id || '',
      });
      setSelectedBox(4);
    }
    if (cell.field === metricTypeEnum.leadSold) {
      testRenderComponent(renderComponentEnum.dataGrid, {
        metricType: metricTypeEnum.leadSold,
        rowDetails: cell.row || {},
        groupId: cell?.row?._id || '',
      });
      setSelectedBox(5);
    }

    if (
      metricFilter === metricTypeEnum.leadContacted ||
      metricFilter === metricTypeEnum.leadSold ||
      metricFilter === metricTypeEnum.opportunityAddedInNurturing
    ) {
      if (cell.field === 'name') {
        testRenderComponent(renderComponentEnum.nurturingHistory, {
          opportunityId: cell?.row?.opportunity_id || '',
          rowDetails: cell.row || {},
          analytics: true,
        });
      }
    } else {
      if (cell.field === 'name') {
        return;
      }
    }
  };

  // Recent Revivals Query Params
  const recentRevivalsParams = {
    dealer: selectedDealerId,
    mysql: mysqlFlag,
    lead_filter: JSON.stringify({ nurturing_status: 'CONTACTED' }),
    ...(timeperiodSelect === 'CUSTOM_RANGE'
      ? { startDate: dates.start_date, endDate: dates.end_date }
      : { timeperiod: timeperiodSelect }),
    skip: 0,
    limit: 20,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  };

  // Recent Sales Influenced Query Params
  const recentSalesInfluencedParams = {
    dealer: selectedDealerId,
    mysql: mysqlFlag,
    lead_filter: JSON.stringify({ nurturing_status: 'LEAD' }),
    ...(timeperiodSelect === 'CUSTOM_RANGE'
      ? { startDate: dates.start_date, endDate: dates.end_date }
      : { timeperiod: timeperiodSelect }),
    skip: 0,
    limit: 20,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  };

  const {
    data: recentRevivalsData,
    isLoading: isRecentRevivalsLoading,
    error: recentRevivalsError,
    isRefetching: isRecentRevivalsRefetching,
    dataUpdatedAt: recentRevivalsUpdatedAt,
    refetch: refetchRecentRevivals,
  } = useQuery({
    queryKey: ['recent-revivals', recentRevivalsParams],
    queryFn: () => Lead.getAllLeadsForDataGrid(recentRevivalsParams),
    enabled: !!selectedDealerId,
    refetchInterval: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const recentRevivalsMapped = Array.isArray(recentRevivalsData?.body.data)
    ? recentRevivalsData?.body.data.map(item => ({
        _id: item._id,
        content: `##title::${item.name || item.first_name || ''}##subtitle::${
          item.latest_contacted_date
            ? dateFormatter(item.latest_contacted_date)
            : ''
        }##tooltip::${item.lead_details_in_a_string || ''}##`,
      }))
    : [];

  const {
    data: recentSalesInfluencedData,
    isLoading: isRecentSalesInfluencedLoading,
    error: recentSalesInfluencedError,
    isRefetching: isRecentSalesInfluencedRefetching,
    dataUpdatedAt: recentSalesInfluencedUpdatedAt,
    refetch: refetchRecentSalesInfluenced,
  } = useQuery({
    queryKey: ['recent-sales-influenced', recentSalesInfluencedParams],
    queryFn: () => Lead.getAllLeadsForDataGrid(recentSalesInfluencedParams),
    enabled: !!selectedDealerId,
    refetchInterval: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const recentSalesInfluencedMapped = Array.isArray(
    recentSalesInfluencedData?.body?.data,
  )
    ? recentSalesInfluencedData?.body.data.map(item => ({
        _id: item._id,
        content: `##title::${item.name || item.first_name || ''}##subtitle::${
          item.latest_lead_active_date
            ? dateFormatter(item.latest_lead_active_date)
            : ''
        }##tooltip::${item.lead_details_in_a_string || ''}##`,
      }))
    : [];

  if (!selectedDealerId) {
    return (
      <Typography
        variant="h4"
        gutterBottom
        component="div"
        className={classes.dealerTypography}
      >
        Please select a dealer
      </Typography>
    );
  }

  return (
    <>
      {' '}
      <Helmet>
        <title>Sales Metric Page</title>
        <meta name="description" content="Sales Metrics View - Myautoiq" />
      </Helmet>
      {selectedDealer.loading ||
      dataGridDataState.loading ||
      taskHtmlState.loading ||
      exportDataState.loading ? (
        <LoadingIndicator />
      ) : null}
      <Box sx={{
        height: 'calc(100vh - 66px)',
        padding: '0 48px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ marginTop: '48px', marginBottom: '48px', display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <BreadcrumbContainer>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<NavigateNextIcon fontSize="small" />}
              >
                {breadcrumbsList?.map((breadcrumb, index) => (
                  <BreadcrumbLinkComponent
                    key={index}
                    underline={breadcrumb.isActive ? 'none' : 'always'}
                    color={breadcrumb.isActive ? 'text.primary' : 'inherit'}
                    onClick={() => handleClickBreadcrumbs(breadcrumb, index)}
                    sx={{ cursor: breadcrumb.isActive ? '' : 'pointer' }}
                  >
                    {breadcrumb.label}
                  </BreadcrumbLinkComponent>
                ))}
              </Breadcrumbs>
            </BreadcrumbContainer>
            <PageHeader sx={{ marginBottom: '0 !important' }}>Autopilot</PageHeader>
          </Box>
          
          {/* Right side container with buttons and toolbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Dashboard Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <ProtectedGroupDashboardButton selectedDealer={selectedDealer} />
              <Button
                variant="outlined"
                size="large"
                disabled={!selectedDealer?.business_dashboard_url}
                onClick={() => {
                  if (selectedDealer?.business_dashboard_url) {
                    window.open(selectedDealer.business_dashboard_url, '_blank');
                  }
                }}
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                  },
                  '&:disabled': {
                    borderColor: 'text.disabled',
                    color: 'text.disabled',
                  },
                }}
              >
                Dashboard
              </Button>
            </Box>
            
            {/* Ask Mia Button */}
            <ProtectedAskMiaButton
              onClick={() => {
                history.push('/ai-avatar-layout/measure/ask-mia');
              }}
            />
            
            {/* Time Period Toolbar */}
            <Suspense fallback={<LoadingIndicator />}>
              <SalesDemoToolbar
                mockApiFlag={mockApiFlag}
                mysqlFlag={mysqlFlag}
                handleToggleMockData={handleToggleMockData}
                handleToggleMysql={handleToggleMysql}
                timeperiod={timeperiod}
                selectedTimeperiod={timeperiodSelect}
                handleClickChangeTimeperiod={handleClickChangeTimeperiod}
                renderComponent={renderComponent}
              />
            </Suspense>
          </Box>
        </Box>

        {/* Executive Summary Section */}
        <Box sx={{ mb: 3 }}>
          <ExecutiveSummary key={selectedDealerId} />
        </Box>

        {(renderComponent === renderComponentEnum.metrics ||
          renderComponent === renderComponentEnum.dataGrid) && (
            <Box sx={{ mb: 3 }}>
              <Suspense fallback={<LoadingIndicator />}>
                <NurturingMetricsComponent
                  metrics={metrics}
                  onClickMetrics={testRenderComponent}
                  selectedMetric={metricFilter}
                  selectedBox={selectedBox}
                  setSelectedBox={setSelectedBox}
                  isLoading={metricsState.loading || summarizationLoading}
                />
              </Suspense>
            </Box>
          
        )}
        {renderComponent === renderComponentEnum.dataGrid && (
          <Box sx={{ mb: 3 }}>
            <DataGridMAIQ
              key={`${metricFilter}-${timeperiodSelect}`}
              type={DataGridTypes.SALES_DEMO(
                renderData,
                mysqlFlag,
                metricFilter,
                timeperiodSelect,
                dates.start_date,
                dates.end_date,
              )}
              onCellClick={handleCellCLick}
            />
          </Box>
        )}
        {/* 
          Note: NurturingHistoryComponent is no longer rendered here.
          We now redirect to a shareable URL instead of rendering the component locally.
          The redirect happens in the testRenderComponent function.
        */}
        {/* Only show carousels if not on nurturing history or data grid page */}
        {isAllowedToViewActivityMetrics() &&
          renderComponent !== renderComponentEnum.nurturingHistory &&
          renderComponent !== renderComponentEnum.dataGrid && (
            <>
              {/* Active Outreach from useQuery */}
              {activeOutreachMapped && activeOutreachMapped.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <CarouselMAIQ
                    key={`${timeperiodSelect}-active-outreach`}
                    title="Active Outreach"
                    data={activeOutreachMapped}
                    isLoading={isActivitiesLoading}
                    isRefetching={isActivitiesRefetching}
                    lastUpdatedAt={activitiesUpdatedAt}
                    onRefresh={refetchActivities}
                    onCardClick={card => {
                      testRenderComponent(
                        renderComponentEnum.nurturingHistory,
                        {
                          metricType: metricTypeEnum.leadContacted,
                          opportunityId: String(card._id),
                          rowDetails: card,
                          isFromActiveOutreach: true,
                        },
                      );
                    }}
                  />
                </Box>
              )}
              {/* TODO: Replace recentRevivalsMapped with activityContext.recentRevivals */}
              {recentRevivalsMapped?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <CarouselMAIQ
                    key={`${timeperiodSelect}-${metricFilter}`}
                    title="Recent Revivals"
                    data={recentRevivalsMapped}
                    isLoading={isRecentRevivalsLoading}
                    isRefetching={isRecentRevivalsRefetching}
                    lastUpdatedAt={recentRevivalsUpdatedAt}
                    onRefresh={refetchRecentRevivals}
                    onCardClick={card => {
                      testRenderComponent(
                        renderComponentEnum.nurturingHistory,
                        {
                          metricType: metricTypeEnum.leadContacted,
                          opportunityId: String(card._id),
                          rowDetails: card,
                        },
                      );
                    }}
                  />
                </Box>
              )}
              {/* TODO: Replace recentSalesInfluencedMapped with activityContext.recentSalesInfluenced */}
              {recentSalesInfluencedMapped.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <CarouselMAIQ
                    key={`${timeperiodSelect}-recent-sales-influenced`}
                    title="Recent Sales Influenced"
                    data={recentSalesInfluencedMapped}
                    isLoading={isRecentSalesInfluencedLoading}
                    isRefetching={isRecentSalesInfluencedRefetching}
                    lastUpdatedAt={recentSalesInfluencedUpdatedAt}
                    onRefresh={refetchRecentSalesInfluenced}
                    onCardClick={card => {
                      testRenderComponent(
                        renderComponentEnum.nurturingHistory,
                        {
                          metricType: metricTypeEnum.leadSold,
                          opportunityId: String(card._id),
                          rowDetails: card,
                        },
                      );
                    }}
                  />
                </Box>
              )}
            </>
          )}
      </Box>
    </>
  );
};

export default MeasurePage;